import ContactForm from "../../components/ContactForm";
import { motion } from "framer-motion";
import { FiMail, FiBriefcase, FiPhone } from "react-icons/fi";

const contactData = [
  {
    id: 1,
    icon: FiMail,
    title: "Email Us",
    link: "hr@nexasoftinfotech.com",
    description: "Send us an email anytime",
    color: "from-primary-500/20 to-primary-600/10",
    borderColor: "border-primary-500/30",
    iconColor: "text-primary-400",
  },
  {
    id: 2,
    icon: FiBriefcase,
    title: "Hiring Now",
    link: "hr@nexasoftinfotech.com",
    description: "Join our amazing team",
    color: "from-secondary-500/20 to-secondary-500/10",
    borderColor: "border-secondary-500/30",
    iconColor: "text-secondary-500",
  },
  {
    id: 3,
    icon: FiPhone,
    title: "Call Us",
    link: "+91 8460720816",
    description: "Mon - Fri, 9:00 AM - 6:00 PM",
    color: "from-emerald-500/20 to-emerald-600/10",
    borderColor: "border-emerald-500/30",
    iconColor: "text-emerald-400",
  },
];

const ContactUs = () => {
  return (
    <section
      className="relative bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 px-p83 py-10 500:py-14 min-h-[90vh] flex flex-col justify-center overflow-hidden"
      style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 120, 209, 0.1), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 164, 56, 0.08), transparent 50%)'
      }}
      id="contact"
    >
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/8 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-500/8 rounded-full blur-3xl translate-y-1/2 translate-x-1/2 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        {/* Header Section */}
        <motion.div
          className="text-center mb-10 500:mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 rounded-full mb-4 backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
            <span className="text-primary-300 font-poppins-semi-bold text-sm">
              Get In Touch
            </span>
          </motion.div>

          <h2 className="font-merry-bold tracking-tight text-2xl 500:text-3xl 700:text-4xl 900:text-5xl mb-3">
            Contact{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-primary-500 font-bold">
                Us
              </span>
              <motion.span
                className="absolute bottom-2 left-0 right-0 h-3 bg-primary-500/20 -z-0 rounded-full"
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </h2>

          <motion.p
            className="text-neutral-300 text-sm 500:text-base max-w-2xl mx-auto font-poppins-regular"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Have a project in mind? Let's discuss how we can help bring your vision to life.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 500:gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4 500:space-y-6">
            {contactData.map(({ id, icon: Icon, title, link, description, color, borderColor, iconColor }, index) => (
              <motion.a
                key={id}
                href={title === "Call Us" ? `tel:${link}` : `mailto:${link}`}
                className={`group block bg-gradient-to-br ${color} border-2 ${borderColor} rounded-2xl p-5 500:p-6 backdrop-blur-sm hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300`}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -4, scale: 1.02 }}
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 500:w-14 500:h-14 bg-gradient-to-br ${color} border ${borderColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 500:w-7 500:h-7 ${iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg 500:text-xl font-poppins-semi-bold text-neutral-100 mb-1 group-hover:text-primary-400 transition-colors duration-300">
                      {title}
                    </h3>
                    <p className="text-sm 500:text-base font-poppins-medium text-primary-400 mb-1 truncate">
                      {link}
                    </p>
                    <p className="text-xs 500:text-sm font-poppins-regular text-neutral-400">
                      {description}
                    </p>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-gradient-to-br from-neutral-800/95 via-neutral-800 to-neutral-900/95 rounded-3xl p-6 500:p-8 shadow-2xl border border-neutral-700/50 backdrop-blur-sm">
              <h3 className="text-xl 500:text-2xl font-merry-bold text-neutral-100 mb-2">
                Send us a <span className="text-primary-500">Message</span>
              </h3>
              <p className="text-sm 500:text-base text-neutral-300 font-poppins-regular mb-6">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              <ContactForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
